import { HashRouter as Router, Route, Routes, Navigate } from "react-router-dom";

import "./tokens.css";
import "./App.css";
import "./components/SubmitJobs/ImportForm.css";
import { Fade, ThemeProvider, createTheme, useMediaQuery } from "@mui/material";
import React, { Suspense, useEffect } from "react";
import { CustomToolbar, CustomTitle, DrawerNavigation, NavLink, SIDEBAR_COLLAPSED_WIDTH } from "./components/Toolbar";
import { LoginForm, Logout, Register, PasswordReset, RegistrationContactNotice } from "./components/authentication";
import { UserContext } from "./hooks/UserContext";
import { useImpersonationContext } from "./hooks/ImpersonationContext";
import { getJwtToken } from "./utilities/utils";
import { setAuthFailureHandler } from "./api/axiosInstance";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CompanyContextProvider } from "./hooks/CompanyContext";
import * as Sentry from "@sentry/react";
import { LoadingComponent } from "./components/helpers";
import HomePage from "./components/Home";
import { API } from "./api";
import { DEFAULT_PAGE } from "./utilities/constants";
import { User } from "./models";

// Lazy-loaded routes — split into separate chunks
const JobsDashboardWithLocalizationProvider = React.lazy(() => import("./components/JobsDashboard/JobsDashboard"));
const JobDetailPage = React.lazy(() =>
	import("./components/JobsDashboard/JobDetail").then((m) => ({ default: m.JobDetailPage })),
);
const JobSubmissionForm = React.lazy(() => import("./components/SubmitJobs/ImportForm"));
const MarkJobReadyPage = React.lazy(() =>
	import("./components/MarkJobsReady/MarkJobReadyPage").then((m) => ({ default: m.MarkJobReadyPage })),
);

// Admin pages — rarely visited, biggest code-splitting win
const AgrianIssues = React.lazy(() =>
	import("./components/admin/AgrianIssues").then((m) => ({ default: m.AgrianIssues })),
);
const DataSendbackIssues = React.lazy(() =>
	import("./components/admin/DataSendbackIssues").then((m) => ({ default: m.DataSendbackIssues })),
);
const PostSamplingIssues = React.lazy(() =>
	import("./components/admin/PostSamplingIssues").then((m) => ({ default: m.PostSamplingIssues })),
);
const GeoFileConverter = React.lazy(() =>
	import("./components/admin/GeoFileConverter").then((m) => ({ default: m.GeoFileConverter })),
);
const ImportSettings = React.lazy(() =>
	import("./components/admin/ImportSettings").then((m) => ({ default: m.ImportSettings })),
);
const MissionControlLogs = React.lazy(() =>
	import("./components/admin/MissionControlLogs").then((m) => ({ default: m.MissionControlLogs })),
);
const MissionControlPasswordsGenerator = React.lazy(() =>
	import("./components/admin/MissionControlPasswordsGenerator").then((m) => ({
		default: m.MissionControlPasswordsGenerator,
	})),
);
const PasswordGenerator = React.lazy(() =>
	import("./components/admin/PasswordGenerator").then((m) => ({ default: m.PasswordGenerator })),
);
const SortShapefiles = React.lazy(() =>
	import("./components/admin/SortShapefiles").then((m) => ({ default: m.SortShapefiles })),
);
const Robots = React.lazy(() => import("./components/admin/Robots").then((m) => ({ default: m.Robots })));
const UnmatchedDeals = React.lazy(() =>
	import("./components/admin/UnmatchedDeals").then((m) => ({ default: m.UnmatchedDeals })),
);
const UnmatchedBranches = React.lazy(() =>
	import("./components/admin/UnmatchedBranches").then((m) => ({ default: m.UnmatchedBranches })),
);

const theme = createTheme({
	palette: {
		primary: {
			main: "#274052",
		},
		secondary: {
			main: "#dc004e",
		},
	},
});

interface ProtectedRouteProps {
	redirectPath?: string;
}
const ProtectedRoute = ({ children, redirectPath }: React.PropsWithChildren<ProtectedRouteProps>) => {
	const jwtToken = getJwtToken();
	if (!jwtToken) {
		return <Navigate to={redirectPath ?? "/login"} replace />;
	}

	return children;
};

function App() {
	// force this to be non-null because we won't use this until our loading screen is done
	// therefore any passed down will certainly be truthy
	const [currentUser, setCurrentUser] = React.useState<User | undefined>();
	const [loading, setLoading] = React.useState(true);
	const [currentLink, setCurrentLink] = React.useState<NavLink>({ to: "/login", text: "Login" });
	const [mobileOpen, setMobileOpen] = React.useState(false);
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const { impersonatedEmail, stopImpersonation } = useImpersonationContext();

	// Register auth failure handler so the interceptor can clear React user state on 401
	useEffect(() => {
		setAuthFailureHandler(() => setCurrentUser(undefined));
	}, []);

	useEffect(() => {
		const token = getJwtToken();
		if (!token) {
			setLoading(false);
			return;
		}
		setCurrentUser(undefined);
		setLoading(true);
		const fetchUser = async () => {
			try {
				const user = await API.Users.me();
				setCurrentUser(user);
			} catch (error: any) {
				// 401 is handled by the axios interceptor (clears tokens + user state)
				// Only handle 403 impersonation case here
				const status = error?.response?.status;
				if (status === 403 && impersonatedEmail) {
					stopImpersonation();
				}
			} finally {
				setLoading(false);
			}
		};
		fetchUser();
	}, [impersonatedEmail]);

	useEffect(() => {
		if (!currentUser) {
			return;
		}
		Sentry.setUser({
			id: currentUser.id,
			email: currentUser.email,
			username: currentUser.fullName,
		});
	}, [currentUser]);

	const handleLogin = (user: User) => {
		setCurrentUser(user);
	};

	return loading ? (
		<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
			<LoadingComponent />
		</div>
	) : (
		<Fade in={!loading}>
			<div>
				<UserContext.Provider value={currentUser}>
					<ThemeProvider theme={theme}>
						<Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
							<CompanyContextProvider user={currentUser}>
								<CustomTitle setCurrentLink={setCurrentLink} />
								<div style={{ height: "100vh", overflow: "hidden" }}>
									<CustomToolbar title={currentLink.text} onMenuClick={() => setMobileOpen(true)} />
									<DrawerNavigation
										onNavigate={(link: NavLink) => {
											setCurrentLink(link);
										}}
										mobileOpen={mobileOpen}
										onMobileClose={() => setMobileOpen(false)}
									/>

									<div
										style={{
											marginLeft: isMobile ? 0 : SIDEBAR_COLLAPSED_WIDTH,
											height: "calc(100vh - 64px)",
											overflowY: "auto",
										}}
									>
										<Suspense fallback={<LoadingComponent />}>
											<Routes>
												<Route path="/home" element={<HomePage setLoggedIn={handleLogin} />} />
												<Route path="/login" element={<LoginForm setLoggedIn={handleLogin} />} />
												<Route path="/register" element={<Register setLoggedIn={handleLogin} />} />
												<Route
													path="/"
													element={
														currentUser ? <Navigate to={DEFAULT_PAGE} /> : <LoginForm setLoggedIn={handleLogin} />
													}
												/>
												<Route path="/forgot-password" element={<PasswordReset setLoggedIn={handleLogin} />} />
												<Route path="/reset-password" element={<PasswordReset setLoggedIn={handleLogin} />} />
												<Route path="/jobs">
													<Route
														index
														path="dashboard"
														element={
															<ProtectedRoute>
																<JobsDashboardWithLocalizationProvider setLoading={setLoading} />
															</ProtectedRoute>
														}
													/>
													<Route
														path="submit"
														element={
															<ProtectedRoute>
																<JobSubmissionForm />
															</ProtectedRoute>
														}
													/>
													<Route
														path="ready"
														element={
															<ProtectedRoute>
																<MarkJobReadyPage setLoading={setLoading} />
															</ProtectedRoute>
														}
													/>
													<Route
														path=":jobId"
														element={
															<ProtectedRoute>
																<JobDetailPage />
															</ProtectedRoute>
														}
													/>
												</Route>
												<Route path="/logout" element={<Logout />} />
												<Route path="/registration-notice" element={<RegistrationContactNotice />} />
												<Route path="/admin">
													<Route
														index
														path="sortshp"
														element={
															<ProtectedRoute>
																<SortShapefiles />
															</ProtectedRoute>
														}
													/>
													<Route
														path="import"
														element={
															<ProtectedRoute>
																<ImportSettings />
															</ProtectedRoute>
														}
													/>
													<Route
														path="geofile-converter"
														element={
															<ProtectedRoute>
																<GeoFileConverter />
															</ProtectedRoute>
														}
													/>
													<Route
														path="mission-control-logs"
														element={
															<ProtectedRoute>
																<MissionControlLogs />
															</ProtectedRoute>
														}
													/>
													<Route
														path="mission-control-passwords-generator"
														element={
															<ProtectedRoute>
																<MissionControlPasswordsGenerator />
															</ProtectedRoute>
														}
													/>
													<Route
														path="password-generator"
														element={
															<ProtectedRoute>
																<PasswordGenerator />
															</ProtectedRoute>
														}
													/>
													<Route
														path="agrian-issues"
														element={
															<ProtectedRoute>
																<AgrianIssues />
															</ProtectedRoute>
														}
													/>
													<Route
														path="data-sendback-issues"
														element={
															<ProtectedRoute>
																<DataSendbackIssues />
															</ProtectedRoute>
														}
													/>
													<Route
														path="post-sampling-issues"
														element={
															<ProtectedRoute>
																<PostSamplingIssues />
															</ProtectedRoute>
														}
													/>
													<Route
														path="robots"
														element={
															<ProtectedRoute>
																<Robots />
															</ProtectedRoute>
														}
													/>
													<Route
														path="unmatched-deals"
														element={
															<ProtectedRoute>
																<UnmatchedDeals />
															</ProtectedRoute>
														}
													/>
													<Route
														path="unmatched-branches"
														element={
															<ProtectedRoute>
																<UnmatchedBranches />
															</ProtectedRoute>
														}
													/>
												</Route>
											</Routes>
										</Suspense>
									</div>
								</div>
							</CompanyContextProvider>
						</Router>
					</ThemeProvider>
				</UserContext.Provider>
				<ToastContainer position="bottom-left" />
			</div>
		</Fade>
	);
}

export default Sentry.withProfiler(App);
